import { EventEmitter } from '@angular/core';
import { ColDef } from 'ag-grid-community';

export enum FilterTemplateType {
    freeform = 'freeform',
    multiChoice = 'multiChoice',
    ownerSingleChoice = 'ownerSingleChoice',
    singleChoice = 'singleChoice',
    date = 'date',
    range = 'range',
    comboBox = 'comboBox',
    clear = 'clear',
    multipleIconsButtonSelection = 'multipleIconsButtonSelection',
    singleIconButtonSelection = 'singleIconButtonSelection'
}

export interface GenericColDef extends ColDef {
    automationId: string;
    headerAutomationId?: string;
    automationIdParsed?: boolean;
    automationIdCustom?: boolean;
    headerAlignDirection?: AlignDirection;
    headerRequired?: boolean;
    cellAlignDirection?: AlignDirection;
    sortBy?: string;
    cellTemplateType?: CellTemplateType;
    filterType?: FilterTemplateType;
    inlineGridFilter?: boolean;
    filterPlaceholder?: string;
    alternativeFieldNames?: {
        iconClass: string;
        iconColor: string;
        tooltip?: string;
    };
    serviceTierFilter?: boolean;
    filterItems?: any;
    filterItemSelected?: any;
    headerClass?: string;
    defaultSortBy?: string;
    defaultSortAscending?: boolean;
    filterAttributeName?: string;
    preserveSpaces?: boolean;
    children?: Array<GenericColDef>;
    headerHeight?: number;
    groupHeaderHeight?: number;
    applyFilterOnLoad?: boolean;
    reviewIcon?: boolean;
    claimsIcon?: boolean;
    addReviewTemplate?: boolean;
}

export class GridContext {
    sortBy: string;
    sortByFieldName = 'sortBy';
    sortAscending: boolean;
    sortAscendingFieldName = 'sortAscending';
    sortEnabled = true;
    sortTypeEnabled = true;
    groupItemsFieldName = 'groupFamilies';
    inlineGridFilter = false;
    filterType = FilterTemplateType.freeform;
    filterData: any;
    loadAndFilter: boolean;
    uiMultiFilter: boolean;
    isFilterNameChangeable: boolean;
    filterTreeStructure: boolean;

    groupItems: (sortBy: string) => boolean;
    onHeaderClick: (sortBy: string, sortAscending: boolean) => void;
    onHeaderContextMenu: (item: ColDef, event: MouseEvent) => void;
    onCellContextMenu: (item: any, event: MouseEvent) => void;
    contextMenuItemMap: (value: any, handler?: any) => void;
    onFilterApply: (item: any, filterType: FilterTemplateType, filterColumn: string, isResetAllFilter?: boolean, applyFilterOnChildren?: boolean, isClearFilter?: boolean) => void;
    onTaskActionClick: (action: CellActionType, item: any) => void;
    clearAllFilters: EventEmitter<any>;
    clearAllFilterInputs: EventEmitter<any>;

    setRequestFields(data: any): void {
        if (data && this.sortBy && this.sortEnabled) {
            if (this.sortBy) {
                data[this.sortByFieldName] = this.sortBy;
            }
            if (this.sortTypeEnabled) {
                data[this.sortAscendingFieldName] = this.sortAscending;
                if (this.sortAscendingFieldName === 'descending' || this.sortAscendingFieldName === 'sortDescending') {
                    data[this.sortAscendingFieldName] = this.sortAscendingFieldName === 'descending' || this.sortAscendingFieldName === 'sortDescending' ? !this.sortAscending : this.sortAscending;
                }
            }
            if (this.groupItems && this.groupItemsFieldName) {
                data[this.groupItemsFieldName] = this.groupItems(this.sortBy);
            }
        }
    }

    constructor(value?: Partial<GridContext>) {
        if (value) {
            Object.assign(this, value);
        }
    }
}

export enum GridRowSelectionType {
    none = 'none',
    single = 'single',
    singleCheckbox = 'singleCheckbox',
    singleCheckboxWithRowClickSelection = 'singleCheckboxWithRowClickSelection',
    multiple = 'multiple',
    multipleCheckbox = 'multipleCheckbox',
    multipleCheckboxWithRowClickSelection = 'multipleCheckboxWithRowClickSelection'
}

export enum SortByFilter {
    SIZE = 'SIZE',
    COUNT = 'COUNT',
    NAME = 'NAME',
    ORG = 'ORG',
    CREATED_BY = 'CREATED_BY',
    DATE_CREATED = 'DATE_CREATED',
    STATUS = 'STATUS',
    CLAUSE_ORDER = 'CLAUSE_ORDER'
}

export enum AlignDirection {
    left = 'left',
    center = 'center',
    right = 'right',
}

export enum CellActionType {
    linkLabelClick = 'linkLabelClick',
    expandCollapse = 'expandCollapse',
    openFamily = 'openFamily',
    openThread = 'openThread',
    checkbox = 'checkbox',
    cellButton = 'cellButton',
    contextMenu = 'contextMenu',
    drillCluster = 'drillCluster',
    subclusterDrill = 'subclusterDrill',
    workbasketIcon = 'workbasketIcon',
    workbasketPause = 'workbasketPause',
    workbasketResume = 'workbasketResume',
    workbasketCancel = 'workbasketCancel',
    workbasketDelete = 'workbasketDelete',
    jobManagementInput = 'jobManagementInput',
    selectStandard = 'selectStandard',
    selectExpress = 'selectExpress',
    selectOcr = 'selectOcr',
    lowPriorityJob = 'lowPriorityJob',
    mediumPriorityJob = 'mediumPriorityJob',
    highPriorityJob = 'highPriorityJob',
    editableInput = 'editableInput',
    workflowStep = 'workflowStep'
}

export enum CellTemplateType {
    basicLabel = 'basicLabel',
    basicLabelWithTooltip = 'basicLabelWithTooltip',
    nameIconLabel = 'nameIconLabel',
    nameMultipleIconsLabel = 'nameMultipleIconsLabel',
    linkIconLabel = 'linkIconLabel',
    disabledLinkLabel = 'disabledLinkLabel',
    boldLabel = 'boldLabel',
    redLabel = 'redLabel',
    readonlyInputLabel = 'readonlyInputLabel',
    longReadonlyInputLabel = 'longReadonlyInputLabel',
    groupIconLabel = 'groupIconLabel',
    date = 'date',
    formattedDateOrToday = 'formattedDateOrToday',
    organizationName = 'organizationName',
    organizationDesc = 'organizationDesc',
    iconSwitch = 'iconSwitch',
    checkbox = 'checkbox',
    disabledCheckbox = 'disabledCheckbox',
    contextMenu = 'contextMenu',
    tagList = 'tagList',
    docListFamilyThread = 'docListFamilyThread',
    roleDropdown = 'roleDropdown',
    roleDropdownByProjOrOrg = 'roleDropdownByProjOrOrg',
    cellButton = 'cellButton',
    tagQueue = 'tagQueue',
    iconCluster = 'iconCluster',
    iconOnlyFieldWithTooltip = 'iconOnlyLabelWithTooltip',
    lock = 'lock',
    detailsReport = 'detailsReport',
    download = 'download',
    userStatus = 'userStatus',
    tpAuthUserStatus = 'tpAuthUserStatus',
    tpAuthUserStatusIconOrLabel = 'tpAuthUserStatusIconOrLabel',
    userAuthenticationMethod = 'userAuthenticationMethod',
    nameIconLabelTooltip = 'nameIconLabelTooltip',
    jobManagementRate = 'jobManagementRate',
    jobManagementCores = 'jobManagementCores',
    jobManagementComplete = 'jobManagementComplete',
    computedDuration = 'computedDuration',
    clusteredTopTerms = 'clusteredTopTerms',
    editableNumericInput = 'editableNumericInput',
    editableReactivatedThesholdInput = 'editableReactivatedThesholdInput',
    editableDeactivatedThesholdInput = 'editableDeactivatedThesholdInput',
    realmNodesType = 'realmNodesType',
    connectorOrDataAreaName = 'connectorOrDataAreaName',
    jobPriorityTypeButtons = 'jobPriorityTypeButtons',
    textWithStatus = 'textWithStatus',
    workflowName = 'workflowName',
    reviewProjectsWithTooltip = 'reviewProjectsWithTooltip'
}
